package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.dsl.ir.model.overlay.ContentRef
import kotlin.jvm.JvmStatic

/**
 * 自动消失的顶层通知元素。
 *
 * 两条触发路径：
 * - 声明式：`toast "id" { visible = ... message = ... }`，由 ToastLayer 直接渲染；duration 字段仅作
 *   元数据，不会触发自动移除（由 visible 控制）。
 * - 内建函数：`toast(message, kind, duration, position)`，由 ToastManager 维护活跃队列，duration 到期
 *   自动移除（duration=0 表示永久）。
 *
 * 本轮简化：不实现淡入淡出动画，duration 到时直接移除；动画统一留给反应式动画系统。
 */
class Toast private constructor() : UIElement<Toast>() {

    private var messageText: String = ""
    private var kindValue: String = DEFAULT_KIND
    private var durationMs: Long = DEFAULT_DURATION
    private var positionValue: String = DEFAULT_POSITION
    private var contentRefValue: ContentRef? = null

    // ========== 逐实例样式覆盖（null = 回退按 kind 解析的 ToastStyle 对应值） ==========
    private var backgroundColorValue: Int? = null
    private var textColorValue: Int? = null
    private var cornerRadiusValue: Float? = null

    init {
        // 与 modal 一致：默认不可见，需 visible=true 才进入 ToastLayer 渲染
        visibleField = false
    }

    protected override fun self(): Toast = this

    fun message(v: String?): Toast {
        messageText = v ?: ""
        return this
    }

    fun message(): String = messageText

    fun kind(v: String?): Toast {
        kindValue = v ?: DEFAULT_KIND
        return this
    }

    fun kind(): String = kindValue

    fun duration(ms: Long): Toast {
        durationMs = ms
        return this
    }

    fun duration(): Long = durationMs

    fun position(v: String?): Toast {
        positionValue = v ?: DEFAULT_POSITION
        return this
    }

    fun position(): String = positionValue

    fun contentRef(v: ContentRef?): Toast {
        contentRefValue = v
        return this
    }

    fun contentRef(): ContentRef? = contentRefValue

    // ========== 逐实例样式覆盖 ==========

    fun backgroundColor(v: Int?): Toast {
        backgroundColorValue = v
        return this
    }

    fun backgroundColor(): Int? = backgroundColorValue

    fun textColor(v: Int?): Toast {
        textColorValue = v
        return this
    }

    fun textColor(): Int? = textColorValue

    fun cornerRadius(v: Float?): Toast {
        cornerRadiusValue = v
        return this
    }

    fun cornerRadius(): Float? = cornerRadiusValue

    companion object {
        /** 默认提示等级。 */
        const val DEFAULT_KIND = "info"

        /** 默认显示时长（毫秒）。 */
        const val DEFAULT_DURATION = 3000L

        /** 默认位置。 */
        const val DEFAULT_POSITION = "top"

        /** 创建一个通知元素。 */
        @JvmStatic
        fun create(): Toast = Toast()
    }
}
